import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { money } from "./utils";
import JournalEntryRow from "./JournalEntryRow";

export default function JournalDayForm({ selectedDate, selected, onChanged }) {
  const [form, setForm] = useState({ kind: "trade", sign: "profit", amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Sila masukkan amount yang sah."); return; }
    let signed = amt;
    if (form.kind === "trade" && form.sign === "loss") signed = -amt;
    setBusy(true);
    try {
      await api.post("/journal", {
        date: selectedDate,
        kind: form.kind,
        amount: signed,
        note: form.note || "",
      });
      toast.success("Entry saved.");
      setForm({ kind: "trade", sign: "profit", amount: "", note: "" });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/journal/${id}`);
    onChanged();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div>
          <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-500">Selected date</div>
          <div className="font-display font-bold text-xl tracking-tight" data-testid="journal-selected-date">{selectedDate}</div>
        </div>
        {selected && (
          <div className={`font-mono-plex font-semibold text-sm ${selected.trade >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
            Trade: {money(selected.trade)}
            {selected.deposit > 0 && <span className="ml-3 text-[#00b4ff]">+${selected.deposit.toFixed(2)} in</span>}
            {selected.withdraw > 0 && <span className="ml-3 text-slate-600">-${selected.withdraw.toFixed(2)} out</span>}
          </div>
        )}
      </div>

      {/* Add entry form */}
      <form onSubmit={submit} className="grid md:grid-cols-6 gap-3 mb-5" data-testid="journal-add-form">
        <select
          data-testid="journal-kind"
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex text-sm"
        >
          <option value="trade">Trade</option>
          <option value="deposit">Deposit</option>
          <option value="withdraw">Withdraw</option>
        </select>
        {form.kind === "trade" ? (
          <select
            data-testid="journal-sign"
            value={form.sign}
            onChange={(e) => setForm({ ...form, sign: e.target.value })}
            className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex text-sm"
          >
            <option value="profit">Profit</option>
            <option value="loss">Loss</option>
          </select>
        ) : <div className="hidden md:block" />}
        <input
          required
          data-testid="journal-amount"
          type="number"
          min="0"
          step="0.01"
          placeholder="Amount (USD)"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex text-sm"
        />
        <input
          data-testid="journal-note"
          placeholder="Note (optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="md:col-span-2 px-3 py-2.5 rounded-lg border border-slate-200 text-sm"
        />
        <button
          data-testid="journal-submit"
          disabled={busy}
          className="bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-semibold text-sm inline-flex items-center justify-center gap-2"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <Plus size={14} /> {busy ? "Saving…" : "Add"}
        </button>
      </form>

      {/* Entries list for selected day */}
      {(!selected || selected.entries.length === 0) ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500 text-center">
          Belum ada entry untuk {selectedDate}. Add one above.
        </div>
      ) : (
        <div className="space-y-2">
          {selected.entries.map((e) => (
            <JournalEntryRow key={e.id} entry={e} onDelete={del} />
          ))}
        </div>
      )}
    </div>
  );
}
